import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { Button, Breadcrumb, Checkbox, Dialog } from 'element-react'
import MaintenanceDialog from './includes/MaintenanceDialog'
import MaintenanceModeDataDialog from './includes/MaintenanceModeDataDialog'
import Spacer from '../views/includes/Spacer'
import { MainSection } from '../views/includes/Wrappers'
import useAdmin from '../../hooks/useAdmin'
import useMisc from '../../hooks/useMisc'
import AppRoutes from '../../routes/appRoutes'
import useGlobalLoading from '../../hooks/useGlobalLoading'

const SetMaintenanceStatus = props => {
    const [showToggleDialog, setShowToggleDialog] = useState(false)
    const [showDataDialog, setShowDataDialog] = useState(false)
    const [showPreviewModal, setShowPreviewModal] = useState(false)
    const { changeLoading } = useGlobalLoading()
    const {
        handleChangeMaintenanceMode,
        handleUpdateMaintenanceModeData,
    } = useAdmin()
    const {
        isInMaintenanceModePage: maintenance,
        maintenanceModeMessage,
        maintenanceModeLink,
        getMaintenanceModeForPage,
        getMaintenanceModeLink,
    } = useMisc()

    useEffect(() => {
        const loadMaintenanceData = async () => {
            changeLoading(true)
            try {
                await Promise.all([
                    getMaintenanceModeForPage(),
                    getMaintenanceModeLink(),
                ])
            } catch (error) {
                console.error('Error loading maintenance data:', error)
            } finally {
                changeLoading(false)
            }
        }

        loadMaintenanceData()
    }, [])

    const onChangedSuccess = async () => {
        try {
            await Promise.all([
                getMaintenanceModeForPage(),
                getMaintenanceModeLink(),
            ])
        } catch (error) {
            console.error('Error reloading maintenance data:', error)
        } finally {
            changeLoading(false)
        }
    }

    const goBack = () => {
        props.history.goBack()
    }

    const cancelToggleRequest = async () => {
        closeToggleDialog()
        try {
            await Promise.all([
                getMaintenanceModeForPage(),
                getMaintenanceModeLink(),
            ])
        } catch (error) {
            console.error('Error reloading maintenance data:', error)
        }
    }

    const cancelDataRequest = async () => {
        closeDataDialog()
        try {
            await Promise.all([
                getMaintenanceModeForPage(),
                getMaintenanceModeLink(),
            ])
        } catch (error) {
            console.error('Error reloading maintenance data:', error)
        }
    }

    const closeToggleDialog = () => {
        setShowToggleDialog(false)
    }

    const closeDataDialog = () => {
        setShowDataDialog(false)
    }

    const toggle = () => {
        changeLoading(true)
        closeToggleDialog()
        handleChangeMaintenanceMode(onChangedSuccess, closeToggleDialog)
    }

    const updateData = data => {
        changeLoading(true)
        closeDataDialog()
        handleUpdateMaintenanceModeData(data, onChangedSuccess, closeDataDialog)
    }

    const handleShowToggleDialog = () => {
        setShowToggleDialog(true)
    }

    const handleShowDataDialog = () => {
        setShowDataDialog(true)
    }

    const handleShowPreview = () => {
        setShowPreviewModal(true)
    }

    const closePreviewModal = () => {
        setShowPreviewModal(false)
    }

    return (
        <MainSection className="admin--add-user">
            <div className="row">
                <div className="columns medium-9">
                    {/* Breadcrumbs */}
                    <Breadcrumb separator="/" className="margin-top-small">
                        <Breadcrumb.Item>
                            <Link to={AppRoutes.home()}>Home</Link>
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>Maintenance Status</Breadcrumb.Item>
                    </Breadcrumb>
                </div>
                <div className="columns medium-3 show-for-medium">
                    {/* Back Button */}
                    <Button
                        type="text"
                        onClick={goBack}
                        className="button-back margin-top-small"
                    >
                        <i className="icon-chevron-thin-left"></i> Go Back
                    </Button>
                </div>
            </div>

            <div className="row">
                <div className="small-12 columns">
                    <Spacer size="small" />

                    <h4 className="text-xl">Maintenance Status</h4>
                    <p className="text-base mt-2 mb-4">
                        Check the box below to turn on Maintenance Mode. When
                        maintenance is complete, uncheck the box to turn off
                        Maintenance Mode and allow customers to resume usage of
                        the front end application.
                    </p>
                    <form onClick={handleShowToggleDialog} className="mb-6">
                        <div
                            style={{
                                transform: 'scale(1.2)',
                                transformOrigin: 'left',
                            }}
                        >
                            <Checkbox checked={maintenance}>
                                <span className="text-base ml-2">
                                    Maintenance Mode
                                </span>
                            </Checkbox>
                        </div>
                    </form>

                    <Spacer size="small" />

                    <h5 className="text-lg">
                        Current Maintenance Message & Link
                    </h5>
                    <p className="text-base mt-2 mb-4">
                        Update the message and link that will be displayed to
                        users when maintenance mode is active.
                    </p>
                    <div className="flex justify-between mt-4">
                        <Button type="primary" onClick={handleShowDataDialog}>
                            Update Maintenance Data
                        </Button>
                        <Button onClick={handleShowPreview}>
                            View Preview
                        </Button>
                    </div>
                </div>
            </div>

            <MaintenanceDialog
                show={showToggleDialog}
                cancel={cancelToggleRequest}
                toggle={toggle}
                isMaintenanceMode={maintenance}
            />

            <MaintenanceModeDataDialog
                show={showDataDialog}
                cancel={cancelDataRequest}
                updateData={updateData}
                currentMessage={maintenanceModeMessage}
                currentLink={maintenanceModeLink}
            />

            <Dialog
                className="maintenance-preview-dialog"
                size="large"
                top="10%"
                title="Maintenance Mode Preview"
                visible={showPreviewModal}
                onCancel={closePreviewModal}
            >
                <Dialog.Body>
                    <div className="row">
                        <div className="small-12 columns">
                            <div className="p-4 bg-gray-100 rounded border border-gray-300">
                                <div className="bg-white p-6 rounded text-center">
                                    <h1 className="text-4xl font-bold mb-4 text-yellow">
                                        Maintenance Mode
                                    </h1>
                                    <p className="text-2xl font-medium max-w-2xl mx-auto">
                                        {maintenanceModeMessage}
                                    </p>
                                    {maintenanceModeLink &&
                                        maintenanceModeLink.hasLink && (
                                            <p className="text-2xl font-medium max-w-2xl mt-4 mx-auto">
                                                <a
                                                    href={
                                                        maintenanceModeLink.link
                                                    }
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-yellow hover:underline"
                                                >
                                                    {
                                                        maintenanceModeLink.linkText
                                                    }
                                                </a>
                                            </p>
                                        )}
                                </div>
                            </div>
                        </div>
                    </div>
                </Dialog.Body>
                <Dialog.Footer className="dialog-footer">
                    <Button onClick={closePreviewModal}>Close</Button>
                </Dialog.Footer>
            </Dialog>
        </MainSection>
    )
}

SetMaintenanceStatus.propTypes = {
    history: PropTypes.shape({
        goBack: PropTypes.func,
    }),
}

export default SetMaintenanceStatus
